import { useState } from 'react';
import { Link } from 'react-router';
import { useBroadcast, useBroadcastPreview, useHasRole, usePlayerAction, usePlayers } from '../api/hooks';
import { SEGMENTS, type PlayerRow, type Segment } from '../api/types';
import { PageHeader } from '../app/Shell';
import { SEGMENT_LABEL, USER_STATUS } from '../app/labels';
import { Badge, Button, Card, ConfirmDialog, ErrorNote, Field, KeyValues, QueryView, ReasonField, SuccessNote, fmtAgo, fmtDateTime, fmtInt, reasonError, toast } from '../ui';
import { PushPreview } from './UserDetail';

interface Draft {
  title: string;
  body: string;
  path: string;
}

const draftOk = (d: Draft) => d.title.trim().length > 0 && d.title.length <= 60 && d.body.trim().length > 0 && d.body.length <= 180 && d.path.startsWith('/');

function DraftFields({ d, onChange }: { d: Draft; onChange: (d: Draft) => void }) {
  return (
    <>
      <Field label="標題" hint={`${d.title.length}/60`}>
        {(id) => <input id={id} type="text" maxLength={60} value={d.title} onChange={(e) => onChange({ ...d, title: e.target.value })} />}
      </Field>
      <Field label="內容" hint={`${d.body.length}/180`}>
        {(id) => <textarea id={id} maxLength={180} rows={3} value={d.body} onChange={(e) => onChange({ ...d, body: e.target.value })} />}
      </Field>
      <Field label="點擊後打開 (path)" hint="App 內的路徑，例如 / 、/manga 或 /store" error={d.path.startsWith('/') ? null : '要以 / 開頭。'}>
        {(id) => <input id={id} type="text" value={d.path} onChange={(e) => onChange({ ...d, path: e.target.value })} />}
      </Field>
    </>
  );
}

/** Pushes from the team: to one player (support) or a broadcast to a segment (owner). All audited and rate-limited. */
export function NotificationsPage() {
  const isOwner = useHasRole('owner');
  return (
    <>
      <PageHeader title="推送通知" description="經「玩家通知」頻道發出；玩家關閉了這類通知就不會收到。發出後不能收回，每次都記錄在審計日誌。" />
      <div className="grid-2">
        <OneUser />
        {isOwner ? (
          <Broadcast />
        ) : (
          <Card title="廣播">
            <p className="faint">廣播只限擁有者角色。</p>
          </Card>
        )}
      </div>
    </>
  );
}

function OneUser() {
  const [q, setQ] = useState('');
  const players = usePlayers(q.trim());
  const [picked, setPicked] = useState<PlayerRow | null>(null);
  const [d, setD] = useState<Draft>({ title: '', body: '', path: '/' });
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState('');
  const send = usePlayerAction();
  const [done, setDone] = useState<string | null>(null);

  return (
    <Card title="推送給一位玩家" sub="每位玩家每小時最多 3 次">
      <div className="stack">
        {picked ? (
          <div className="spread">
            <span>
              <strong>{picked.characterName ?? picked.email ?? '訪客'}</strong> <code>{picked.publicId}</code> <Badge tone={USER_STATUS[picked.status].tone}>{USER_STATUS[picked.status].label}</Badge>
            </span>
            <Button size="sm" variant="ghost" onClick={() => setPicked(null)}>
              改選
            </Button>
          </div>
        ) : (
          <>
            <Field label="玩家" hint="電郵、玩家 ID、名字或 user ID">
              {(id) => <input id={id} type="search" value={q} onChange={(e) => setQ(e.target.value)} />}
            </Field>
            <QueryView query={players} loading="搜尋中…">
              {(rows) =>
                rows.length ? (
                  <div className="stack" style={{ gap: 4 }}>
                    {rows.slice(0, 6).map((r) => (
                      <Button key={r._id} variant="ghost" size="sm" style={{ justifyContent: 'flex-start' }} onClick={() => setPicked(r)}>
                        {r.characterName ?? r.displayName ?? '—'} · {r.email ?? '訪客'} · {r.publicId}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p className="faint small">找不到玩家。</p>
                )
              }
            </QueryView>
          </>
        )}
        <DraftFields d={d} onChange={setD} />
        <PushPreview title={d.title} body={d.body} />
        {done && <SuccessNote>{done}</SuccessNote>}
        <div className="row">
          <Button variant="primary" disabled={!picked || !draftOk(d)} onClick={() => setConfirm(true)}>
            預覽並發出…
          </Button>
        </div>
      </div>
      <ConfirmDialog
        open={confirm}
        title="發出這則推送？"
        confirmLabel="發出"
        busy={send.isPending}
        canConfirm={!reasonError(reason)}
        onCancel={() => setConfirm(false)}
        onConfirm={() =>
          picked &&
          send.mutate(
            { userId: picked._id, action: 'notify', body: { title: d.title.trim(), body: d.body.trim(), path: d.path, reason: reason.trim() } },
            {
              onSuccess: (r) => {
                setConfirm(false);
                setReason('');
                setDone(`已發出到 ${fmtInt(Number(r['devices'] ?? 0))} 部裝置。`);
                toast('推送已發出');
              },
            },
          )
        }
        error={send.isError ? <ErrorNote error={send.error} /> : null}
      >
        <p className="small">
          對象：<Link to={`/users/${picked?._id}`}>{picked?.characterName ?? picked?.email ?? picked?._id}</Link>
        </p>
        <PushPreview title={d.title} body={d.body} />
        <ReasonField value={reason} onChange={setReason} autoFocus />
      </ConfirmDialog>
    </Card>
  );
}

function Broadcast() {
  const [segment, setSegment] = useState<Segment>('active7');
  const preview = useBroadcastPreview(segment, true);
  const [d, setD] = useState<Draft>({ title: '', body: '', path: '/' });
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const send = useBroadcast();
  const waitUntil = preview.data?.nextAllowedAt && Date.parse(preview.data.nextAllowedAt) > Date.now() ? preview.data.nextAllowedAt : null;
  const reach = preview.data?.reachable ?? 0;

  return (
    <Card title="廣播" sub="每小時最多一次 · 只限擁有者">
      <div className="stack">
        <Field label="對象">
          {(id) => (
            <select id={id} value={segment} onChange={(e) => setSegment(e.target.value as Segment)}>
              {SEGMENTS.map((s) => (
                <option key={s} value={s}>
                  {SEGMENT_LABEL[s]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <QueryView query={preview} loading="計算人數…">
          {(p) => (
            <KeyValues
              items={[
                ['符合的玩家', fmtInt(p.players)],
                ['有推送裝置', <strong>{fmtInt(p.reachable)}</strong>],
                ['上次廣播', p.lastBroadcastAt ? `${fmtDateTime(p.lastBroadcastAt)}（${fmtAgo(p.lastBroadcastAt)}）` : '—'],
              ]}
            />
          )}
        </QueryView>
        <DraftFields d={d} onChange={setD} />
        <PushPreview title={d.title} body={d.body} />
        {waitUntil && <p className="small" style={{ color: 'var(--warn)' }}>要等到 {fmtDateTime(waitUntil)} 才可以再廣播。</p>}
        {send.isSuccess && <SuccessNote>已排隊發給 {fmtInt(send.data.recipients)} 位玩家。</SuccessNote>}
        <div className="row">
          <Button variant="danger" disabled={!draftOk(d) || !!waitUntil || reach === 0} onClick={() => setConfirm(true)}>
            預覽並廣播…
          </Button>
        </div>
      </div>
      <ConfirmDialog
        open={confirm}
        title={`廣播給 ${fmtInt(reach)} 位玩家？`}
        tone="danger"
        confirmLabel="廣播"
        busy={send.isPending}
        canConfirm={!reasonError(reason) && typed.trim() === String(reach)}
        onCancel={() => setConfirm(false)}
        onConfirm={() =>
          send.mutate(
            { segment, title: d.title.trim(), body: d.body.trim(), path: d.path, reason: reason.trim() },
            {
              onSuccess: () => {
                setConfirm(false);
                setReason('');
                setTyped('');
                toast('廣播已排隊發出');
              },
            },
          )
        }
        error={send.isError ? <ErrorNote error={send.error} /> : null}
      >
        <p className="small">
          對象：{SEGMENT_LABEL[segment]}（有推送裝置的 {fmtInt(reach)} 位）。發出後不能收回。
        </p>
        <PushPreview title={d.title} body={d.body} />
        <Field label={`輸入人數 ${reach} 以確認`}>{(id) => <input id={id} type="text" inputMode="numeric" value={typed} onChange={(e) => setTyped(e.target.value)} />}</Field>
        <ReasonField value={reason} onChange={setReason} />
      </ConfirmDialog>
    </Card>
  );
}
